import { useCallback } from "react";
import { yupResolver } from "@hookform/resolvers/yup";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";

import { FormSelectField } from "@covertree/ui/forms/FormSelectField";
import { useFormWithNotification } from "@covertree/hooks";
import { FormConditionRadioGroup, FormTextField } from "@covertree/ui";

import { useGetClaimDetailsForLossType } from "@hooks/useGetClaimDetailsForLossType";
import { useAppDispatch } from "@redux/hooks";
import { LossType, SourceOfDamageWater } from "@api/operations.generated";
import { StepPath } from "@routing/paths";
import { submitClaimDetails } from "@redux/slices";

import { ClaimsForm } from "../../ClaimsForm";
import { validation, WaterDamageSourceForm } from "./form";

const sourceOfDamageOptions = [
  { label: "Appliance", value: SourceOfDamageWater.Appliance },
  { label: "Plumbing", value: SourceOfDamageWater.Plumbing },
  {
    label: "Freezing Pipes",
    value: SourceOfDamageWater.FreezingPipes,
  },
  { label: "Roof", value: SourceOfDamageWater.Roof },
  {
    label: "Sump pump/Sewer backup",
    value: SourceOfDamageWater.SumpPumpOrSewerBackup,
  },
  { label: "Other", value: SourceOfDamageWater.Other },
];

export function WaterDamage() {
  const dispatch = useAppDispatch();

  const defaultValues = useGetClaimDetailsForLossType();

  const form = useFormWithNotification({
    mode: "all",
    reValidateMode: "onSubmit",
    defaultValues: { ...defaultValues },
    resolver: yupResolver(validation),
  });

  const { control } = form;

  const submit = useCallback((value: WaterDamageSourceForm) => {
    dispatch(
      submitClaimDetails({
        value,
        type: LossType.WaterDamage,
      })
    );
  }, []);

  return (
    <ClaimsForm
      form={form}
      onSubmit={submit}
      navigateToStep={StepPath.UPLOAD_CLAIM_DOCUMENTS}
      dataTestId="water-damage">
      <Grid item xs={12}>
        <FormSelectField
          id="claim-type"
          name="claimType"
          control={control}
          options={sourceOfDamageOptions}
          label="Source of Damage"
          dataTestId="claim-type-input"
          placeholder="Select..."
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <FormTextField
          id="claim-description"
          dataTestId="claim-description"
          name="claimDescription"
          rows={4}
          multiline
          label="Please provide a description of the loss and how it happened?"
          control={control}
        />
      </Grid>
      <Grid item xs={12}>
        <Box pt={3}>
          <FormConditionRadioGroup
            id="is-damage-fixed"
            name="isDamageFixed"
            control={control}
            label="Has the source of the water damage been repaired?"
          />
        </Box>
      </Grid>
    </ClaimsForm>
  );
}
